import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import FloatingButtons from '../../../components/feature/FloatingButtons';

export default function BlogPost5() {
  useEffect(() => {
    window.scrollTo(0, 0);

    // SEO meta tags
    document.title = 'Fusion Splicing Nedir? Fiber Optik Ek Teknolojisi | Fiber Ek';

    const updateMetaTag = (name: string, content: string, property?: boolean) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    updateMetaTag('description', 'Fusion splicing (kaynaklı ek) nedir, nasıl çalışır? Fiber optik kabloların birleştirilmesinde kullanılan en kaliteli yöntem hakkında detaylı rehber.');
    updateMetaTag('keywords', 'fusion splicing, fiber optik ek, kaynaklı ek, fiber sonlandırma, OTDR test, fiber kablo tamiri');
    updateMetaTag('author', 'Fiber Ek');
    updateMetaTag('robots', 'index, follow');

    // Open Graph tags
    updateMetaTag('og:title', 'Fusion Splicing Nedir? Fiber Optik Ek Teknolojisi | Fiber Ek', true);
    updateMetaTag('og:description', 'Fusion splicing (kaynaklı ek) nedir, nasıl çalışır? Fiber optik kabloların birleştirilmesinde kullanılan en kaliteli yöntem hakkında detaylı rehber.', true);
    updateMetaTag('og:url', `${import.meta.env.VITE_SITE_URL}/blog/fusion-splicing-nedir`, true);
    updateMetaTag('og:type', 'article', true);
    updateMetaTag('og:locale', 'tr_TR', true);
    updateMetaTag('og:image', 'https://public.readdy.ai/ai/img_res/2b03ee0544a228c76921548304814a31.jpg', true);

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', 'Fusion Splicing Nedir? Fiber Optik Ek Teknolojisi | Fiber Ek');
    updateMetaTag('twitter:description', 'Fusion splicing (kaynaklı ek) nedir, nasıl çalışır? Fiber optik kabloların birleştirilmesinde kullanılan en kaliteli yöntem hakkında detaylı rehber.');
    updateMetaTag('twitter:image', 'https://readdy.ai/api/search-image?query=fusion%20splicing%20machine%20welding%20fiber%20optic%20cables%20close-up%20view%20with%20blue%20arc%20light%20precision%20equipment%20and%20fiber%20strands%20technical%20photography%20clean%20white%20background&width=1200&height=600&seq=blog5detail&orientation=landscape');

    // Canonical link
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `${import.meta.env.VITE_SITE_URL}/blog/fusion-splicing-nedir`;

    // JSON-LD Schema for BlogPosting
    const schema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "Fusion Splicing Nedir? Fiber Optik Ek Teknolojisi",
      "description": "Fusion splicing (kaynaklı ek) nedir, nasıl çalışır? Fiber optik kabloların birleştirilmesinde kullanılan en kaliteli yöntem hakkında detaylı rehber.",
      "image": "https://readdy.ai/api/search-image?query=fusion%20splicing%20machine%20welding%20fiber%20optic%20cables%20close-up%20view%20with%20blue%20arc%20light%20precision%20equipment%20and%20fiber%20strands%20technical%20photography%20clean%20white%20background&width=1200&height=600&seq=blog5detail&orientation=landscape",
      "datePublished": "2024-05-05",
      "dateModified": "2024-05-05",
      "author": {
        "@type": "Organization",
        "name": "Fiber Ek"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Fiber Ek",
        "logo": {
          "@type": "ImageObject",
          "url": `${import.meta.env.VITE_SITE_URL}/logo.png`
        }
      },
      "url": `${import.meta.env.VITE_SITE_URL}/blog/fusion-splicing-nedir`,
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `${import.meta.env.VITE_SITE_URL}/blog/fusion-splicing-nedir`
      }
    };

    let script = document.querySelector('script[type="application/ld+json"]');
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);

    return () => {
      // Cleanup on unmount
      if (canonical && canonical.parentNode) {
        canonical.parentNode.removeChild(canonical);
      }
      if (script && script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <FloatingButtons />

      {/* Hero Section */}
      <section className="relative pt-32 pb-12 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <Link to="/blog" className="inline-flex items-center gap-2 text-teal-400 hover:text-teal-300 mb-6 transition-colors">
              <i className="ri-arrow-left-line"></i>
              <span className="font-medium">Blog'a Dön</span>
            </Link>
            
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Fusion Splicing Nedir? Fiber Optik Ek Teknolojisi
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 text-slate-300">
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  <span>5 Mayıs 2024</span>
                </div>
                <div className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  <span>9 dakika okuma</span>
                </div>
                <div className="flex items-center gap-1">
                  <i className="ri-folder-line"></i>
                  <span>Fiber Optik</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="py-12 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://readdy.ai/api/search-image?query=fusion%20splicing%20machine%20welding%20fiber%20optic%20cables%20close-up%20view%20with%20blue%20arc%20light%20precision%20equipment%20and%20fiber%20strands%20technical%20photography%20clean%20white%20background&width=1200&height=600&seq=blog5detail&orientation=landscape"
                alt="Fusion Splicing"
                className="w-full h-auto object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg max-w-none">
              
              <p className="text-xl text-slate-700 leading-relaxed mb-8 font-medium">
                Fusion splicing (kaynaklı ek), fiber optik kabloların birleştirilmesinde kullanılan en kaliteli ve güvenilir yöntemdir. İstanbul, Gebze, Kocaeli ve çevre illerdeki profesyonel fiber optik kurulum ve tamir işlemlerinde fusion splicing teknolojisi standart olarak kullanılmaktadır.
              </p>

              <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">Fusion Splicing Nedir?</h2>
              
              <p className="text-slate-700 leading-relaxed mb-6">
                Fusion splicing, iki fiber optik kablonun uçlarının elektrik arkı ile ısıtılarak kaynaştırılması ve kalıcı olarak birleştirilmesi işlemidir. Bu yöntemde:
              </p>

              <div className="bg-gradient-to-br from-teal-50 to-slate-50 p-8 rounded-2xl mb-8 border border-teal-100">
                <ul className="space-y-4 text-slate-700">
                  <li className="flex items-start gap-3">
                    <i className="ri-flashlight-fill text-teal-500 text-xl mt-1"></i>
                    <span>İki fiber ucu hassas bir şekilde hizalanır (mikron seviyesinde)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-fire-fill text-teal-500 text-xl mt-1"></i>
                    <span>Elektrik arkı ile yaklaşık 2000°C sıcaklıkta ısıtılır</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-links-fill text-teal-500 text-xl mt-1"></i>
                    <span>Cam molekülleri kaynaşarak tek parça haline gelir</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-shield-check-fill text-teal-500 text-xl mt-1"></i>
                    <span>Ek noktası koruyucu kılıf ile mekanik olarak güçlendirilir</span>
                  </li>
                </ul>
              </div>

              <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">Fusion Splicing Nasıl Çalışır?</h2>

              <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">1. Fiber Hazırlık Aşaması</h3>
              
              <p className="text-slate-700 leading-relaxed mb-6">
                Başarılı bir fusion splice için fiber uçlarının mükemmel hazırlanması gerekir:
              </p>

              <div className="space-y-6 mb-8">
                <div className="bg-white border-2 border-slate-200 p-6 rounded-xl">
                  <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                    <span className="w-8 h-8 bg-teal-500 text-white rounded-lg flex items-center justify-center text-sm">1</span>
                    Kablo Soyma (Stripping)
                  </h4>
                  <p className="text-slate-700 text-sm leading-relaxed">
                    Fiber kablonun dış kılıfı, güçlendirme elemanları ve buffer coating (tampon kaplama) özel soyma aletleri ile dikkatlice çıkarılır. Fiber çekirdeği açığa çıkarılır. Soyma uzunluğu genellikle 3-5 cm'dir.
                  </p>
                </div>

                <div className="bg-white border-2 border-slate-200 p-6 rounded-xl">
                  <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                    <span className="w-8 h-8 bg-teal-500 text-white rounded-lg flex items-center justify-center text-sm">2</span>
                    Fiber Temizliği (Cleaning)
                  </h4>
                  <p className="text-slate-700 text-sm leading-relaxed">
                    Soyulan fiber yüzeyi izopropil alkol ve lint-free (tüy bırakmayan) mendil ile temizlenir. Toz, yağ ve kirden arındırılır. Temizlik kalitesi ek başarısını doğrudan etkiler.
                  </p>
                </div>

                <div className="bg-white border-2 border-slate-200 p-6 rounded-xl">
                  <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                    <span className="w-8 h-8 bg-teal-500 text-white rounded-lg flex items-center justify-center text-sm">3</span>
                    Fiber Kesme (Cleaving)
                  </h4>
                  <p className="text-slate-700 text-sm leading-relaxed">
                    Precision cleaver (hassas kesici) ile fiber ucu 90 derece açıyla kesilir. Kesim yüzeyi ayna gibi düz ve pürüzsüz olmalıdır. Kötü kesim yüksek kayıp ve hatalı ek nedenidir. Kesim kalitesi mikroskop altında kontrol edilir.
                  </p>
                </div>
              </div>

              <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">2. Fusion Splicer Cihazı Kurulumu</h3>
              
              <p className="text-slate-700 leading-relaxed mb-6">
                Hazırlanan fiber uçları fusion splicer cihazına yerleştirilir:
              </p>

              <div className="bg-slate-50 p-6 rounded-xl mb-8 border border-slate-200">
                <ul className="space-y-3 text-slate-700">
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-teal-500 mt-1"></i>
                    <span>Her iki fiber ucu cihazın fiber tutucularına (fiber holder) yerleştirilir</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-teal-500 mt-1"></i>
                    <span>Fiber uçları elektrot çubuklarının ortasına konumlandırılır</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-teal-500 mt-1"></i>
                    <span>Cihaz kapağı kapatılır ve hava akımı engellenir</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-teal-500 mt-1"></i>
                    <span>Uygun splice programı seçilir (SM, MM, OM3, OM4 vb.)</span>
                  </li>
                </ul>
              </div>

              <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">3. Otomatik Hizalama (Alignment)</h3>
              
              <p className="text-slate-700 leading-relaxed mb-6">
                Modern fusion splicer cihazları otomatik hizalama teknolojisine sahiptir:
              </p>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gradient-to-br from-teal-50 to-slate-50 p-6 rounded-xl border border-teal-100">
                  <div className="w-12 h-12 bg-teal-500 text-white rounded-xl flex items-center justify-center mb-4">
                    <i className="ri-focus-3-line text-2xl"></i>
                  </div>
                  <h4 className="font-bold text-slate-900 mb-2">Core Alignment</h4>
                  <p className="text-slate-700 text-sm leading-relaxed">
                    Cihaz kameralar ile fiber çekirdeklerini (core) görüntüler ve X-Y-Z eksenlerinde mikron hassasiyetinde hizalar. En yüksek kalite sağlar. Single mode fiberler için şarttır.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-teal-50 to-slate-50 p-6 rounded-xl border border-teal-100">
                  <div className="w-12 h-12 bg-teal-500 text-white rounded-xl flex items-center justify-center mb-4">
                    <i className="ri-contrast-2-line text-2xl"></i>
                  </div>
                  <h4 className="font-bold text-slate-900 mb-2">Cladding Alignment</h4>
                  <p className="text-slate-700 text-sm leading-relaxed">
                    Fiber dış yüzeyini (cladding) referans alarak hizalar. Daha ekonomik cihazlarda kullanılır. Multi mode fiberler için yeterlidir. Core alignment kadar hassas değildir.
                  </p>
                </div>
              </div>

              <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">4. Kaynaştırma İşlemi (Fusion)</h3>
              
              <p className="text-slate-700 leading-relaxed mb-6">
                Hizalama tamamlandıktan sonra kaynaştırma başlar:
              </p>

              <div className="bg-gradient-to-br from-teal-50 to-slate-50 p-8 rounded-2xl mb-8 border border-teal-100">
                <h4 className="text-xl font-bold text-slate-900 mb-4">Kaynaştırma Aşamaları:</h4>
                <ol className="space-y-4 text-slate-700">
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center font-bold text-sm">1</span>
                    <div>
                      <strong className="block mb-1">Pre-Fusion (Ön Kaynak)</strong>
                      <span>Düşük güçte elektrik arkı ile fiber uçları temizlenir ve yumuşatılır. Yüzey kusurları giderilir.</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center font-bold text-sm">2</span>
                    <div>
                      <strong className="block mb-1">Gap Closing (Aralık Kapatma)</strong>
                      <span>Fiber uçları birbirine doğru hareket ettirilir. Aralık 10-20 mikron'a düşürülür.</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center font-bold text-sm">3</span>
                    <div>
                      <strong className="block mb-1">Main Fusion (Ana Kaynak)</strong>
                      <span>Yüksek güçte elektrik arkı (2000°C) ile fiberler kaynaştırılır. İşlem 2-3 saniye sürer.</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center font-bold text-sm">4</span>
                    <div>
                      <strong className="block mb-1">Annealing (Tavlama)</strong>
                      <span>Düşük güçte ısıtma ile iç gerilimler giderilir. Ek noktası güçlendirilir.</span>
                    </div>
                  </li>
                </ol>
              </div>

              <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">5. Kayıp Ölçümü ve Değerlendirme</h3>
              
              <p className="text-slate-700 leading-relaxed mb-6">
                Kaynaştırma tamamlandıktan sonra cihaz otomatik olarak ek kaybını ölçer:
              </p>

              <div className="overflow-x-auto mb-8">
                <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
                  <thead>
                    <tr className="bg-slate-900 text-white">
                      <th className="p-4 text-left font-semibold">Fiber Tipi</th>
                      <th className="p-4 text-left font-semibold">Tipik Kayıp</th>
                      <th className="p-4 text-left font-semibold">Maksimum Kabul Edilebilir</th>
                      <th className="p-4 text-left font-semibold">Değerlendirme</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    <tr className="border-b border-slate-200">
                      <td className="p-4 font-semibold text-slate-900">Single Mode</td>
                      <td className="p-4 text-slate-700">0.02 - 0.05 dB</td>
                      <td className="p-4 text-slate-700">0.10 dB</td>
                      <td className="p-4 text-green-600 font-semibold">Mükemmel</td>
                    </tr>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <td className="p-4 font-semibold text-slate-900">Multi Mode OM3</td>
                      <td className="p-4 text-slate-700">0.05 - 0.10 dB</td>
                      <td className="p-4 text-slate-700">0.15 dB</td>
                      <td className="p-4 text-green-600 font-semibold">Çok İyi</td>
                    </tr>
                    <tr className="border-b border-slate-200">
                      <td className="p-4 font-semibold text-slate-900">Multi Mode OM4</td>
                      <td className="p-4 text-slate-700">0.05 - 0.10 dB</td>
                      <td className="p-4 text-slate-700">0.15 dB</td>
                      <td className="p-4 text-green-600 font-semibold">Çok İyi</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-xl mb-8">
                <div className="flex items-start gap-3">
                  <i className="ri-alert-line text-amber-600 text-2xl mt-1"></i>
                  <div>
                    <h4 className="font-bold text-amber-900 mb-2">Önemli Not:</h4>
                    <p className="text-amber-800 leading-relaxed">
                      Ek kaybı kabul edilebilir değerin üzerinde ise, ek tekrarlanmalıdır. Fiber uçları yeniden kesilir ve splice işlemi tekrar yapılır. Kaliteli bir ek için acele edilmemeli, her adım özenle tamamlanmalıdır.
                    </p>
                  </div>
                </div>
              </div>

              <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">6. Ek Koruma (Protection)</h3>
              
              <p className="text-slate-700 leading-relaxed mb-6">
                Kaynaşmış ek noktası mekanik olarak zayıftır ve korunması gerekir:
              </p>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-white border-2 border-slate-200 p-6 rounded-xl">
                  <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mb-4">
                    <i className="ri-shield-line text-2xl text-teal-600"></i>
                  </div>
                  <h4 className="font-bold text-slate-900 mb-2">Heat Shrink Sleeve</h4>
                  <p className="text-slate-700 text-sm leading-relaxed mb-3">
                    Isıyla daralan koruyucu kılıf ek noktasına geçirilir ve ısıtılır. Mekanik dayanım sağlar ve su geçirmez hale getirir.
                  </p>
                  <p className="text-slate-600 text-xs">
                    <strong>Tipler:</strong> Standart (40 mm), Uzun (60 mm), Zırhlı (stainless steel rod içerir)
                  </p>
                </div>

                <div className="bg-white border-2 border-slate-200 p-6 rounded-xl">
                  <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mb-4">
                    <i className="ri-fire-line text-2xl text-teal-600"></i>
                  </div>
                  <h4 className="font-bold text-slate-900 mb-2">Isıtma İşlemi</h4>
                  <p className="text-slate-700 text-sm leading-relaxed mb-3">
                    Fusion splicer'ın entegre ısıtıcısı veya ayrı bir oven ile sleeve ısıtılır. 30-60 saniye sürer.
                  </p>
                  <p className="text-slate-600 text-xs">
                    <strong>Sıcaklık:</strong> 100-120°C, otomatik kontrollü
                  </p>
                </div>
              </div>

              <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">Fusion Splicing vs Mechanical Splicing</h2>

              <div className="overflow-x-auto mb-8">
                <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
                  <thead>
                    <tr className="bg-slate-900 text-white">
                      <th className="p-4 text-left font-semibold">Özellik</th>
                      <th className="p-4 text-left font-semibold">Fusion Splicing</th>
                      <th className="p-4 text-left font-semibold">Mechanical Splicing</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    <tr className="border-b border-slate-200">
                      <td className="p-4 font-semibold text-slate-900">Ek Kaybı</td>
                      <td className="p-4 text-green-600">0.02-0.05 dB (Çok Düşük)</td>
                      <td className="p-4 text-orange-600">0.1-0.5 dB (Orta)</td>
                    </tr>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <td className="p-4 font-semibold text-slate-900">Yansıma</td>
                      <td className="p-4 text-green-600">Yok veya çok düşük</td>
                      <td className="p-4 text-orange-600">Var (0.3-0.5 dB)</td>
                    </tr>
                    <tr className="border-b border-slate-200">
                      <td className="p-4 font-semibold text-slate-900">Dayanıklılık</td>
                      <td className="p-4 text-green-600">Çok yüksek (kalıcı)</td>
                      <td className="p-4 text-orange-600">Orta (mekanik)</td>
                    </tr>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <td className="p-4 font-semibold text-slate-900">Ömür</td>
                      <td className="p-4 text-green-600">20+ yıl</td>
                      <td className="p-4 text-orange-600">5-10 yıl</td>
                    </tr>
                    <tr className="border-b border-slate-200">
                      <td className="p-4 font-semibold text-slate-900">Maliyet</td>
                      <td className="p-4 text-orange-600">Yüksek (cihaz pahalı)</td>
                      <td className="p-4 text-green-600">Düşük</td>
                    </tr>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <td className="p-4 font-semibold text-slate-900">Süre</td>
                      <td className="p-4 text-orange-600">5-10 dakika</td>
                      <td className="p-4 text-green-600">1-2 dakika</td>
                    </tr>
                    <tr className="border-b border-slate-200">
                      <td className="p-4 font-semibold text-slate-900">Uygulama</td>
                      <td className="p-4 text-green-600">Kalıcı kurulumlar</td>
                      <td className="p-4 text-orange-600">Geçici çözümler</td>
                    </tr>
                    <tr className="bg-slate-50">
                      <td className="p-4 font-semibold text-slate-900">Önerilen</td>
                      <td className="p-4 text-green-600 font-bold">✓ Profesyonel İşler</td>
                      <td className="p-4 text-orange-600">Acil Onarımlar</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">Konnektör Sonlandırma Tipleri</h2>

              <p className="text-slate-700 leading-relaxed mb-6">
                Fusion splicing sonrası fiber kablolar çeşitli konnektör tipleri ile sonlandırılabilir:
              </p>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-slate-50 p-6 rounded-xl border border-slate-200">
                  <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                    <i className="ri-plug-line text-teal-500 text-xl"></i>
                    SC Konnektör
                  </h4>
                  <p className="text-slate-700 text-sm leading-relaxed mb-2">
                    <strong>Subscriber Connector:</strong> Kare şekilli, push-pull mekanizması. En yaygın kullanılan tip.
                  </p>
                  <p className="text-slate-600 text-xs">
                    <strong>Kullanım:</strong> Patch panel, media converter, switch portları
                  </p>
                </div>

                <div className="bg-slate-50 p-6 rounded-xl border border-slate-200">
                  <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                    <i className="ri-plug-line text-teal-500 text-xl"></i>
                    LC Konnektör
                  </h4>
                  <p className="text-slate-700 text-sm leading-relaxed mb-2">
                    <strong>Lucent Connector:</strong> Küçük form faktör, yüksek yoğunluk. SC'nin yarı boyutunda.
                  </p>
                  <p className="text-slate-600 text-xs">
                    <strong>Kullanım:</strong> Yüksek yoğunluklu patch panel, SFP modüller
                  </p>
                </div>

                <div className="bg-slate-50 p-6 rounded-xl border border-slate-200">
                  <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                    <i className="ri-plug-line text-teal-500 text-xl"></i>
                    ST Konnektör
                  </h4>
                  <p className="text-slate-700 text-sm leading-relaxed mb-2">
                    <strong>Straight Tip:</strong> Yuvarlak şekilli, bayonet kilitleme. Eski tip ama hala kullanılıyor.
                  </p>
                  <p className="text-slate-600 text-xs">
                    <strong>Kullanım:</strong> Eski network ekipmanları, multi mode uygulamalar
                  </p>
                </div>

                <div className="bg-slate-50 p-6 rounded-xl border border-slate-200">
                  <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                    <i className="ri-plug-line text-teal-500 text-xl"></i>
                    MTP/MPO Konnektör
                  </h4>
                  <p className="text-slate-700 text-sm leading-relaxed mb-2">
                    <strong>Multi-fiber Push On:</strong> 12-24 fiber tek konnektörde. Çok yüksek yoğunluk.
                  </p>
                  <p className="text-slate-600 text-xs">
                    <strong>Kullanım:</strong> Veri merkezi, 40G/100G uygulamalar
                  </p>
                </div>
              </div>

              <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">Profesyonel Fusion Splicing Hizmeti</h2>

              <p className="text-slate-700 leading-relaxed mb-6">
                <Link to="/fiber-hizmetleri/fiber-sonlandirma" className="text-teal-600 hover:text-teal-700 font-semibold">Profesyonel fiber sonlandırma hizmetimiz</Link> ile İstanbul, Gebze, Tuzla, Kocaeli ve çevre illerde:
              </p>

              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="text-center p-6 bg-gradient-to-br from-teal-50 to-slate-50 rounded-xl border border-teal-100">
                  <div className="w-16 h-16 bg-teal-500 text-white rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <i className="ri-tools-line text-3xl"></i>
                  </div>
                  <h4 className="font-bold text-slate-900 mb-2">Son Teknoloji Ekipman</h4>
                  <p className="text-slate-600 text-sm">
                    Core alignment fusion splicer, OTDR, power meter ve tüm profesyonel araçlar
                  </p>
                </div>

                <div className="text-center p-6 bg-gradient-to-br from-teal-50 to-slate-50 rounded-xl border border-teal-100">
                  <div className="w-16 h-16 bg-teal-500 text-white rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <i className="ri-user-star-line text-3xl"></i>
                  </div>
                  <h4 className="font-bold text-slate-900 mb-2">Sertifikalı Teknisyenler</h4>
                  <p className="text-slate-600 text-sm">
                    10+ yıl deneyimli, uluslararası sertifikalı fiber optik uzmanları
                  </p>
                </div>

                <div className="text-center p-6 bg-gradient-to-br from-teal-50 to-slate-50 rounded-xl border border-teal-100">
                  <div className="w-16 h-16 bg-teal-500 text-white rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <i className="ri-file-chart-line text-3xl"></i>
                  </div>
                  <h4 className="font-bold text-slate-900 mb-2">Test ve Dokümantasyon</h4>
                  <p className="text-slate-600 text-sm">
                    Her ek için OTDR raporu, kayıp ölçümü ve detaylı dokümantasyon
                  </p>
                </div>
              </div>

              <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">Sık Sorulan Sorular</h2>

              <div className="space-y-4 mb-12">
                <details className="bg-slate-50 p-6 rounded-xl border border-slate-200 group">
                  <summary className="font-bold text-slate-900 cursor-pointer list-none flex items-center justify-between">
                    <span>Fusion splicing ne kadar sürer?</span>
                    <i className="ri-arrow-down-s-line text-2xl text-teal-500 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="text-slate-700 mt-4 leading-relaxed">
                    Tek bir fiber ek işlemi 5-10 dakika sürer. Bu süre fiber hazırlık, hizalama, kaynaştırma ve koruma aşamalarını içerir. 12 fiber kablo için toplam 1-2 saat, 48 fiber kablo için 3-4 saat sürebilir.
                  </p>
                </details>

                <details className="bg-slate-50 p-6 rounded-xl border border-slate-200 group">
                  <summary className="font-bold text-slate-900 cursor-pointer list-none flex items-center justify-between">
                    <span>Fusion splicing maliyeti nedir?</span>
                    <i className="ri-arrow-down-s-line text-2xl text-teal-500 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="text-slate-700 mt-4 leading-relaxed">
                    Maliyet fiber sayısı, kablo tipi ve lokasyona göre değişir. Fiber başına ortalama 50-100 TL arasındadır. Toplu işlerde indirim uygulanır. Ücretsiz keşif hizmetimiz ile detaylı fiyat teklifi sunuyoruz.
                  </p>
                </details>

                <details className="bg-slate-50 p-6 rounded-xl border border-slate-200 group">
                  <summary className="font-bold text-slate-900 cursor-pointer list-none flex items-center justify-between">
                    <span>Fusion splice ömrü ne kadardır?</span>
                    <i className="ri-arrow-down-s-line text-2xl text-teal-500 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="text-slate-700 mt-4 leading-relaxed">
                    Profesyonel yapılmış fusion splice 20+ yıl ömre sahiptir. Kalıcı bir bağlantıdır ve zamanla performans kaybı olmaz. Mekanik hasara karşı korunduğu sürece sonsuz ömürlüdür.
                  </p>
                </details>

                <details className="bg-slate-50 p-6 rounded-xl border border-slate-200 group">
                  <summary className="font-bold text-slate-900 cursor-pointer list-none flex items-center justify-between">
                    <span>Hangi durumlarda fusion splicing kullanılır?</span>
                    <i className="ri-arrow-down-s-line text-2xl text-teal-500 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="text-slate-700 mt-4 leading-relaxed">
                    Fusion splicing kalıcı kurulumlar, <Link to="/blog/fiber-optik-kablo-tamiri-nasil-yapilir" className="text-teal-600 hover:text-teal-700 font-semibold">fiber kablo tamiri</Link>, uzun mesafe bağlantılar ve yüksek performans gerektiren uygulamalarda kullanılır. Telekom, veri merkezi, fabrika ve kampüs ağlarında standarttır.
                  </p>
                </details>
              </div>

            </div>

            {/* CTA Section */}
            <div className="mt-16 p-8 bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl text-center">
              <h3 className="text-3xl font-bold text-white mb-4">
                Profesyonel Fusion Splicing Hizmeti
              </h3>
              <p className="text-slate-300 text-lg mb-6">
                Sertifikalı teknisyenler ve son teknoloji ekipman ile hizmetinizdeyiz
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <a
                  href="tel:+905537165561"
                  className="px-8 py-4 bg-teal-500 hover:bg-teal-600 text-white font-semibold rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-teal-500/50 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line mr-2"></i>
                  +90 553 716 55 61
                </a>
                <a
                  href="https://wa.me/905537165561"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-4 bg-white hover:bg-slate-50 text-slate-900 font-semibold rounded-xl transition-all duration-300 hover:shadow-lg whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line mr-2"></i>
                  WhatsApp Destek
                </a>
              </div>
            </div>

            {/* İlgili Hizmetler */}
            <div className="mt-12">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">İlgili Hizmetlerimiz</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <Link to="/fiber-hizmetleri/fiber-sonlandirma" className="group flex items-center gap-4 p-5 bg-teal-50 border border-teal-200 rounded-xl hover:bg-teal-500 hover:border-teal-500 transition-all duration-300">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-500 group-hover:bg-white rounded-xl flex-shrink-0">
                    <i className="ri-flashlight-line text-2xl text-white group-hover:text-teal-500"></i>
                  </div>
                  <div>
                    <div className="font-bold text-slate-900 group-hover:text-white">Fusion Splicing & Sonlandırma</div>
                    <div className="text-sm text-slate-600 group-hover:text-teal-100">Profesyonel fiber ek ve konnektör işlemleri</div>
                  </div>
                  <i className="ri-arrow-right-line ml-auto text-teal-500 group-hover:text-white"></i>
                </Link>
                <Link to="/fiber-hizmetleri/fiber-kablo-tamiri" className="group flex items-center gap-4 p-5 bg-teal-50 border border-teal-200 rounded-xl hover:bg-teal-500 hover:border-teal-500 transition-all duration-300">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-500 group-hover:bg-white rounded-xl flex-shrink-0">
                    <i className="ri-tools-line text-2xl text-white group-hover:text-teal-500"></i>
                  </div>
                  <div>
                    <div className="font-bold text-slate-900 group-hover:text-white">Fiber Kablo Tamiri</div>
                    <div className="text-sm text-slate-600 group-hover:text-teal-100">Profesyonel fiber optik onarım hizmeti</div>
                  </div>
                  <i className="ri-arrow-right-line ml-auto text-teal-500 group-hover:text-white"></i>
                </Link>
                <Link to="/fiber-hizmetleri/bina-fiber" className="group flex items-center gap-4 p-5 bg-teal-50 border border-teal-200 rounded-xl hover:bg-teal-500 hover:border-teal-500 transition-all duration-300">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-500 group-hover:bg-white rounded-xl flex-shrink-0">
                    <i className="ri-building-line text-2xl text-white group-hover:text-teal-500"></i>
                  </div>
                  <div>
                    <div className="font-bold text-slate-900 group-hover:text-white">Bina Fiber Altyapı</div>
                    <div className="text-sm text-slate-600 group-hover:text-teal-100">Binalarda fiber optik kurulum</div>
                  </div>
                  <i className="ri-arrow-right-line ml-auto text-teal-500 group-hover:text-white"></i>
                </Link>
                <Link to="/fiber-hizmetleri/otdr-test" className="group flex items-center gap-4 p-5 bg-teal-50 border border-teal-200 rounded-xl hover:bg-teal-500 hover:border-teal-500 transition-all duration-300">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-500 group-hover:bg-white rounded-xl flex-shrink-0">
                    <i className="ri-line-chart-line text-2xl text-white group-hover:text-teal-500"></i>
                  </div>
                  <div>
                    <div className="font-bold text-slate-900 group-hover:text-white">OTDR Test Hizmeti</div>
                    <div className="text-sm text-slate-600 group-hover:text-teal-100">Fiber optik test ve sertifikasyon</div>
                  </div>
                  <i className="ri-arrow-right-line ml-auto text-teal-500 group-hover:text-white"></i>
                </Link>
              </div>
            </div>

            {/* Related Articles */}
            <div className="mt-16">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">İlgili Makaleler</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <Link to="/blog/fiber-optik-kablo-tamiri-nasil-yapilir" className="group">
                  <div className="bg-slate-50 rounded-xl p-6 border border-slate-200 hover:border-teal-500 transition-all hover:shadow-lg">
                    <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-teal-500 transition-colors">
                      <i className="ri-tools-line text-2xl text-teal-600 group-hover:text-white"></i>
                    </div>
                    <h4 className="font-bold text-slate-900 mb-2 group-hover:text-teal-600">Fiber Kablo Tamiri</h4>
                    <p className="text-slate-600 text-sm">Profesyonel fiber optik kablo tamir rehberi</p>
                  </div>
                </Link>

                <Link to="/blog/otdr-testi-nedir" className="group">
                  <div className="bg-slate-50 rounded-xl p-6 border border-slate-200 hover:border-teal-500 transition-all hover:shadow-lg">
                    <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-teal-500 transition-colors">
                      <i className="ri-line-chart-line text-2xl text-teal-600 group-hover:text-white"></i>
                    </div>
                    <h4 className="font-bold text-slate-900 mb-2 group-hover:text-teal-600">OTDR Testi</h4>
                    <p className="text-slate-600 text-sm">Fiber optik arıza tespit yöntemi</p>
                  </div>
                </Link>

                <Link to="/blog/fabrikalarda-fiber-altyapi-rehberi" className="group">
                  <div className="bg-slate-50 rounded-xl p-6 border border-slate-200 hover:border-teal-500 transition-all hover:shadow-lg">
                    <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-teal-500 transition-colors">
                      <i className="ri-building-line text-2xl text-teal-600 group-hover:text-white"></i>
                    </div>
                    <h4 className="font-bold text-slate-900 mb-2 group-hover:text-teal-600">Fabrika Fiber Altyapı</h4>
                    <p className="text-slate-600 text-sm">Endüstriyel tesislerde fiber kurulumu</p>
                  </div>
                </Link>
              </div>
            </div>

          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}